import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { LinkedInAuth } from "@/components/auth/LinkedInAuth";
import { CSOLoadingSpinner } from "@/components/ui/cso-loading-spinner";
import { Building, Users } from "lucide-react";

const TeamMemberSignup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [organizationInfo, setOrganizationInfo] = useState<any>(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: ""
  });

  const orgId = searchParams.get('org');
  const inviteCode = searchParams.get('invite');

  useEffect(() => {
    const verifyInvitation = async () => {
      if (!orgId || !inviteCode) {
        toast({
          title: "Invalid Invitation",
          description: "The invitation link is invalid or expired.",
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      try {
        // Verify invitation exists and get organization info
        const { data: invitation } = await supabase
          .from('organization_memberships')
          .select(`
            *,
            organizations (
              name,
              tier,
              branding
            )
          `)
          .eq('id', inviteCode)
          .eq('organization_id', orgId)
          .is('joined_at', null)
          .single();

        if (!invitation) {
          throw new Error('Invitation not found or already used');
        }

        setOrganizationInfo(invitation);
        
        // Pre-fill email if it's in the invitation
        if (invitation.invited_email) {
          setFormData(prev => ({ ...prev, email: invitation.invited_email }));
        }
      } catch (error: any) {
        console.error('Error verifying invitation:', error);
        toast({
          title: "Invalid Invitation",
          description: error.message || "The invitation link is invalid or expired.",
          variant: "destructive",
        });
        navigate('/');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyInvitation();
  }, [orgId, inviteCode, navigate, toast]);

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Sign up the user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
          }
        }
      });

      if (authError) throw authError;

      if (authData.user) {
        // Update the invitation with the new user
        const { error: updateError } = await supabase
          .from('organization_memberships')
          .update({
            user_id: authData.user.id,
            joined_at: new Date().toISOString()
          })
          .eq('id', inviteCode);

        if (updateError) throw updateError;

        toast({
          title: "Welcome to the Team!",
          description: `You've successfully joined ${organizationInfo.organizations.name}. Please check your email to verify your account.`,
        });

        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Error during signup:', error);
      toast({
        title: "Signup Failed",
        description: error.message || "Failed to create account",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CSOLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-cso-100 mb-4">
            {organizationInfo?.organizations?.tier === 'enterprise' ? 
              <Building className="h-6 w-6 text-cso-600" /> : 
              <Users className="h-6 w-6 text-cso-600" />
            }
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900">
            Join {organizationInfo?.organizations?.name}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            You've been invited to join this {organizationInfo?.organizations?.tier} organization
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create Your Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <LinkedInAuth />
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleEmailSignup} className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Create a secure password"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-cso-600 hover:bg-cso-700"
              >
                {isLoading ? "Creating Account..." : "Join Organization"}
              </Button>
            </form>

            <p className="text-xs text-center text-gray-500">
              By joining, you agree to our Terms of Service and Privacy Policy
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeamMemberSignup;
